import React, { useState } from 'react';

import { formatPaise, formatServiceDateShort } from '../../../utils/money';

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';
const btnDanger = 'px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-60';

/**
 * ReverseChargeDialog (C3 · day-1 S5) — post a reversing entry against a
 * POSTED charge. A reversal ADDS a matching minus line; the original stays on
 * record (nothing is edited or deleted — copy never says "edit"). Reason is
 * required; the new balance is restated before confirming. Rendered via the
 * in-app modal — NOT window.confirm.
 *
 * Same-business-day self-serve window (S5 AC4): the ROW's reverse control is
 * disabled-with-reason for a non-admin once the charge is a day old
 * (`RunningBillPanel`'s `isPastOrgDay`) so this dialog only opens for a
 * reversible line. The SERVER remains authoritative — a raced 403
 * `REVERSAL_WINDOW_ELAPSED` (clock drift / stale render) surfaces inline here
 * with the same copy rather than a silent failure. A concurrent 409
 * `ALREADY_REVERSED` is treated as done (the parent refreshes).
 */
export default function ReverseChargeDialog({ charge, balancePaise, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const newBalancePaise = (Number(balancePaise) || 0) - (Number(charge.amount_paise) || 0);

  const submit = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true);
    setError(null);
    const res = await onSubmit({ reason: reason.trim() });
    setBusy(false);
    if (res && !res.success && res.code !== 'ALREADY_REVERSED') {
      setError(
        res.code === 'REVERSAL_WINDOW_ELAPSED'
          ? 'Charges from an earlier day can only be reversed by an admin.'
          : (res.error || 'Could not reverse the charge. Try again.'),
      );
    }
    // ALREADY_REVERSED (or success) → the parent closes + refreshes.
  };

  return (
    <div className={overlayCls} data-testid="reverse-charge-dialog">
      <div className={panelCls}>
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Reverse this charge?</h3>
          <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[12.5px] text-slate-600 dark:text-slate-300 px-3 py-2">
            <b className="text-slate-900 dark:text-white">{charge.description}</b> · {formatServiceDateShort(charge.service_date)} · {charge.qty} × {formatPaise(charge.unit_price_paise)} = <b className="text-slate-900 dark:text-white">{formatPaise(charge.amount_paise)}</b>
            <div className="text-[11.5px] mt-1">A reversal adds a matching minus line to the bill. The original stays on record — nothing is deleted, and a charge can be reversed only once.</div>
          </div>

          {error && (
            <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="reverse-charge-error">{error}</div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1" htmlFor="rv-reason">Reason for reversal (required)</label>
            <textarea id="rv-reason" className="w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder="Why is this charge being reversed?" />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5">
            <span className="text-[13px] text-slate-600 dark:text-slate-300">Balance after reversal</span>
            <b className="text-[15px] tabular-nums text-slate-900 dark:text-white" data-testid="reverse-new-balance">{formatPaise(newBalancePaise)}</b>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Keep charge</button>
          <button type="button" className={btnDanger} onClick={submit} disabled={!reason.trim() || busy} data-testid="reverse-charge-confirm">
            {busy ? 'Reversing…' : 'Reverse this charge'}
          </button>
        </div>
      </div>
    </div>
  );
}
